import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Params } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SchedulerAncComponent } from 'src/app/components/scheduler-anc/scheduler-anc.component';
import { OutButtonsComponent } from 'src/app/components/scheduler-anc/out-buttons/out-buttons.component';
import { ModalHolidayCalendarComponent } from 'src/app/components/ui/modal-holiday-calendar/modal-holiday-calendar.component';
import { AncDataService } from 'src/app/services/ANC/anc-data.service';
import { DeviceService } from 'src/app/services/devices/device.service';
import { getRelayOutputCount } from 'src/app/services/devices/device-type.util';
import { DlDataService } from 'src/app/services/DL/dl-data.service';
import {
  DL_DAYS_OF_WEEK,
  DL_MINUTES_PER_DAY,
  DlProgram,
  DlProgramKind,
  DlScheduleService,
} from 'src/app/services/DL/dl-schedule.service';
import { ModalService } from 'src/app/services/modal/modal.service';
import { SuntimesService } from 'src/app/services/sunTimes/sunTimesLegacy.service';
import tzLookup from 'tz-lookup';

export interface DlProgramEdit extends DlProgram {
  timeStr: string;
  endTimeStr: string;
  pulseDurationSeconds: number;
  cycleOnSeconds: number;
  cycleOffSeconds: number;
  cycleEndDaymask: number;
}

@Component({
    selector: 'app-dl-programs',
    imports: [CommonModule, FormsModule, TranslateModule, OutButtonsComponent, SchedulerAncComponent],
    templateUrl: './dl-programs.component.html',
    styleUrl: './dl-programs.component.scss'
})
export class DlProgramsComponent implements OnInit, OnDestroy {
  @Input() allSettings = false;

  readonly daysOfWeek = DL_DAYS_OF_WEEK;
  private readonly weekdayKeys = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  device = '';
  outputCount = 2;
  selectedOutput: 1 | 2 = 1;
  programDataFormated: DlProgramEdit[] = [];
  programdataFormatedOriginal: DlProgramEdit[] = [];
  scheduleData: any[] = [];
  activePrograms: DlProgramEdit[] = [];
  changesProgram = false;
  changesHoliday = false;
  editProgramIndex: number | null = null;
  editProgramDraft: DlProgramEdit | null = null;
  holidayData: any = { enable: false, periods: [] };
  holidayEnable = false;
  originalHolidayData: any = null;
  viewMode: 'scheduler' | 'holiday' = 'scheduler';
  newProgram = false;
  error = '';
  successMessage = false;
  loading = false;
  highlightedProgramIndex: number | null = null;
  hoveredProgramId: number | null = null;
  editedProgramIds = new Set<number>();
  todayPeriod: any = {};
  todayHours = '--';
  annualHours = '--';
  sunTimesData: any[] = [];
  coordenadas: any = null;
  gmtMinutes = 0;
  timeZone = '';
  selectedSunDate = '';
  dataSeason: any = null;
  ancDeviceInfo: any = null;

  private highlightTimeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private route: ActivatedRoute,
    private _device: DeviceService,
    private _ancDataService: AncDataService,
    private _dlDataService: DlDataService,
    private _dlScheduleService: DlScheduleService,
    private _modalService: ModalService,
    private suntimesService: SuntimesService,
    private translate: TranslateService,
  ) {}

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }

  getDayLabel(dayIndex: number): string {
    const key = this.weekdayKeys[dayIndex];
    return key ? this.t(key) : '';
  }

  ngOnInit(): void {
    this.route.parent?.parent?.params.subscribe((params: Params) => {
      this.device = params['serial'];
      this.outputCount = getRelayOutputCount(this.device);
      if (this.outputCount < 2) {
        this.selectedOutput = 1;
      }
      this.loadData();
      this.loadDeviceLocation();
    });
  }

  ngOnDestroy(): void {
    if (this.highlightTimeoutId) {
      clearTimeout(this.highlightTimeoutId);
      this.highlightTimeoutId = null;
    }
  }

  private loadDeviceLocation(): void {
    if (!this.device) return;

    this._device.getModuleInfo(this.device, 'device').subscribe((deviceInfo) => {
      this.ancDeviceInfo = deviceInfo.cfg;

      this._device.getModuleInfo(this.device, 'seasons').subscribe((data) => {
        this.dataSeason = data.cfg;

        this._device.getDeviceInfo(this.device).subscribe((deviceData) => {
          const version = deviceData.hardware_version?.split('.').pop() ?? '';
          this.coordenadas = this._ancDataService.getLocation(
            version,
            this.ancDeviceInfo.country,
            this.ancDeviceInfo.city
          );

          const now = new Date();
          try {
            this.timeZone = tzLookup(this.coordenadas?.latitude ?? 0, this.coordenadas?.longitude ?? 0);
          } catch {
            this.timeZone = '';
          }

          this.gmtMinutes = this.dataSeason?.enable == 0
            ? this.coordenadas?.gmtWinterMinutes
            : this.coordenadas?.gmtSummerMinutes;

          this.selectedSunDate = this.getDateInputValueForTimeZone(now, this.timeZone);
          this.refreshSunTimesForSelectedDate();
        });
      });
    });
  }

  private refreshSunTimesForSelectedDate(): void {
    const baseDate = this.getDateFromInput(this.selectedSunDate);
    if (!baseDate) return;

    if (!this.coordenadas || !Number.isFinite(Number(this.gmtMinutes))) {
      this.sunTimesData = Array.from({ length: 7 }, (_, index) => {
        const date = new Date(baseDate);
        date.setDate(baseDate.getDate() + index);
        return { date };
      });
      return;
    }

    this.sunTimesData = this.suntimesService.calculateSuntimesLegacyForWeek(
      baseDate,
      this.gmtMinutes,
      this.coordenadas?.latitude ?? 0,
      this.coordenadas?.longitude ?? 0
    );
  }

  private getDateFromInput(value: string): Date | null {
    if (typeof value !== 'string') return null;
    const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return null;

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    if (![year, month, day].every(Number.isFinite)) return null;

    const date = new Date(year, month - 1, day, 12, 0, 0, 0);
    if (
      date.getFullYear() !== year ||
      date.getMonth() !== (month - 1) ||
      date.getDate() !== day
    ) {
      return null;
    }

    return date;
  }

  private getDateInputValue(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private getDateInputValueForTimeZone(date: Date, timeZone: string): string {
    if (!timeZone) return this.getDateInputValue(date);

    try {
      const dtf = new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });

      const parts = dtf.formatToParts(date);
      const values: Record<string, string> = {};
      for (const part of parts) {
        if (part.type !== 'literal') values[part.type] = part.value;
      }

      const year = Number(values.year);
      const month = Number(values.month);
      const day = Number(values.day);
      if (![year, month, day].every(Number.isFinite)) return this.getDateInputValue(date);

      return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    } catch {
      return this.getDateInputValue(date);
    }
  }

  private loadData(): void {
    this._device.getModuleInfo(this.device, 'holiday').subscribe((data) => {
      const formattedHolidayData = this._ancDataService.formatHolidayData(data.cfg);
      const hasEnabledPeriods = (formattedHolidayData.periods || []).some(
        (period: any) => Number(period?.enable ?? period?.enable) === 1
      );

      this.holidayData = {
        ...formattedHolidayData,
        enable: hasEnabledPeriods,
      };
      this.originalHolidayData = JSON.parse(JSON.stringify(this.holidayData));
      this.holidayEnable = hasEnabledPeriods;
      this.todayPeriod = this._ancDataService.isTodayInAnyPeriod(this.holidayData?.periods);
      this.refreshScheduleData();
    });

    this._device.getModuleInfo(this.device, 'schedule').subscribe((data) => {
      const parsed = this._dlDataService.formatProgramData(data.cfg);
      this.programDataFormated = parsed.map((program) => this.enrichProgram(program));
      this.programdataFormatedOriginal = JSON.parse(JSON.stringify(this.programDataFormated));
      this.touchProgramData();
      this.changesProgram = false;
    });
  }

  onOutputChange(output: number): void {
    this.selectedOutput = output === 2 && this.outputCount > 1 ? 2 : 1;
    this.refreshScheduleData();
  }

  goToTodaySunDate(): void {
    this.selectedSunDate = this.getDateInputValueForTimeZone(new Date(), this.timeZone);
    this.refreshSunTimesForSelectedDate();
  }

  shiftSunDate(days: number): void {
    const base = this.getDateFromInput(this.selectedSunDate);
    if (!base) return;

    const next = new Date(base);
    next.setDate(next.getDate() + Math.trunc(days || 0));
    this.selectedSunDate = this.getDateInputValue(next);
    this.refreshSunTimesForSelectedDate();
  }

  onSunDateChange(): void {
    this.refreshSunTimesForSelectedDate();
  }

  onSchedulerDragChange(change: {
    programId: number;
    time: number;
    valueKind: 'absolute' | 'offset';
    isFinal?: boolean;
    dlKind?: 'pulse' | 'cycle' | 'switch';
    dlEdge?: 'start' | 'end';
  }): void {
    const programId = Number(change?.programId);
    const nextTime = Math.trunc(Number(change?.time) || 0);
    const isFinal = change?.isFinal === true;

    if (!Number.isFinite(programId) || change.valueKind !== 'absolute') return;

    const updatedIndex = (this.programDataFormated || []).findIndex(
      (program) => Number(program?.id) === programId
    );
    if (updatedIndex < 0) return;

    const program = this.programDataFormated[updatedIndex];
    const minutes = Math.max(0, Math.min(DL_MINUTES_PER_DAY - 1, nextTime));
    let updated: DlProgramEdit = { ...program };

    if (change.dlKind === 'cycle' && change.dlEdge === 'end') {
      const [endHi, endLo] = this._dlScheduleService.encodeEndMinuteBytes(minutes);
      const auxdata = [...(program.auxdata || [])];
      while (auxdata.length < 9) auxdata.push(0);

      auxdata[1] = endHi;
      auxdata[2] = endLo;

      updated = {
        ...program,
        auxdata,
        endTimeStr: this._dlScheduleService.formatMinuteLabel(minutes),
      };
    } else {
      updated = {
        ...program,
        time: minutes,
        timeStr: this._dlScheduleService.formatMinuteLabel(minutes),
      };
    }

    this.programDataFormated[updatedIndex] = updated;

    if (isFinal) {
      this.touchProgramData(true);
      this.markProgramAsEdited(programId);
      this.highlightProgramRow(updatedIndex);
    }
  }

  onProgramRowEnter(item: DlProgramEdit): void {
    const id = Number(item?.id);
    this.hoveredProgramId = Number.isFinite(id) && id >= 0 ? id : null;
  }

  onProgramRowLeave(): void {
    this.hoveredProgramId = null;
  }

  isDaySelected(daymask: unknown, dayValue: number): boolean {
    const mask = Number(daymask) || 0;
    return (mask & dayValue) !== 0;
  }

  toggleDay(program: DlProgramEdit | null, dayValue: number): void {
    if (!program) return;
    const mask = Number(program.daymask) || 0;
    program.daymask = (mask ^ dayValue) & 0x7f;
  }

  toggleCycleEndDay(program: DlProgramEdit | null, dayValue: number): void {
    if (!program) return;
    const mask = Number(program.cycleEndDaymask) || 0;
    program.cycleEndDaymask = (mask ^ dayValue) & 0x7f;
  }

  setQuickPreset(type: 'weekdays' | 'weekend' | 'allDays', target: 'start' | 'end' = 'start'): void {
    if (!this.editProgramDraft) return;

    const presetDayMask: Record<'weekdays' | 'weekend' | 'allDays', number> = {
      weekdays: 64 + 32 + 16 + 8 + 4,
      weekend: 2 + 1,
      allDays: 127,
    };

    if (target === 'end') {
      this.editProgramDraft.cycleEndDaymask = presetDayMask[type];
      return;
    }

    this.editProgramDraft.daymask = presetDayMask[type];
  }

  setEditProgramIndex(index: number): void {
    this.editProgramIndex = index;
    const item = this.programDataFormated?.[index];
    this.editProgramDraft = item ? this.enrichProgram(item) : null;
    if (this.editProgramDraft && this.outputCount < 2) {
      this.editProgramDraft.channel = 1;
    }
  }

  saveEditProgram(): void {
    if (this.editProgramIndex === null || !this.editProgramDraft) return;

    const current = this.programDataFormated?.[this.editProgramIndex];
    if (!current) return;

    const type = Number(this.editProgramDraft.type) || 0;
    const mode = this._dlScheduleService.normalizeModeForType(type);
    const startMinutes = this.readTimeInputMinutes('editDlProgramStartTime', this.editProgramDraft.timeStr) ?? 0;
    const auxdata = new Array(9).fill(0);

    if (type === 2) {
      auxdata[0] = Math.max(0, Math.trunc(Number(this.editProgramDraft.pulseDurationSeconds) || 0));
    } else if (type === 3) {
      const endMinutes = this.readTimeInputMinutes('editDlProgramEndTime', this.editProgramDraft.endTimeStr) ?? 0;
      const cycleEndDaymask = (
        Number(this.editProgramDraft.cycleEndDaymask) ||
        Number(this.editProgramDraft.daymask) ||
        0
      ) & 0x7f;
      const cycleOnSeconds = Math.max(1, Math.trunc(Number(this.editProgramDraft.cycleOnSeconds) || 60));
      const cycleOffSeconds = Math.max(1, Math.trunc(Number(this.editProgramDraft.cycleOffSeconds) || 60));
      const [endHi, endLo] = this._dlScheduleService.encodeEndMinuteBytes(endMinutes);
      const [on0, on1, on2] = this._dlScheduleService.encodeSeconds24(cycleOnSeconds);
      const [off0, off1, off2] = this._dlScheduleService.encodeSeconds24(cycleOffSeconds);

      auxdata[0] = cycleEndDaymask;
      auxdata[1] = endHi;
      auxdata[2] = endLo;
      auxdata[3] = on0;
      auxdata[4] = on1;
      auxdata[5] = on2;
      auxdata[6] = off0;
      auxdata[7] = off1;
      auxdata[8] = off2;

      this.editProgramDraft.cycleEndDaymask = cycleEndDaymask;
      this.editProgramDraft.cycleOnSeconds = cycleOnSeconds;
      this.editProgramDraft.cycleOffSeconds = cycleOffSeconds;
      this.editProgramDraft.endTimeStr = this._dlScheduleService.formatMinuteLabel(endMinutes);
    }

    const nextProgram: DlProgram = {
      id: current.id,
      channel: this.outputCount > 1
        ? (Number(this.editProgramDraft.channel) || current.channel)
        : 1,
      type,
      mode,
      daymask: (Number(this.editProgramDraft.daymask) || 0) & 0x7f,
      festive: Number(this.editProgramDraft.festive) || 0,
      time: startMinutes,
      auxdata,
      kind: this._dlScheduleService.resolveKindFromType(type, mode),
    };

    this.programDataFormated[this.editProgramIndex] = this.enrichProgram(nextProgram);
    this.touchProgramData(true);
    this.markProgramAsEdited(Number(nextProgram.id));
    this.highlightProgramRow(this.editProgramIndex);
    this.newProgram = false;
  }

  addProgram(): void {
    const nextId = this.getNextProgramId();
    const newProgram = this.enrichProgram({
      id: nextId,
      channel: this.outputCount > 1 ? 3 : 1,
      type: 1,
      mode: 16,
      daymask: 127,
      festive: 0,
      time: 0,
      auxdata: new Array(9).fill(0),
      kind: 'on',
    });

    this.programDataFormated.push(newProgram);
    this.touchProgramData(true);
    this.markProgramAsEdited(nextId);
    this.highlightProgramRow(this.programDataFormated.length - 1);
    this.setEditProgramIndex(this.programDataFormated.length - 1);
    this.newProgram = true;
  }

  deleteProgram(index: number): void {
    if (!Array.isArray(this.programDataFormated)) return;
    if (index < 0 || index >= this.programDataFormated.length) return;

    this.programDataFormated[index] = this.enrichProgram({
      id: -1,
      channel: 0,
      type: 0,
      mode: 0,
      daymask: 0,
      festive: 0,
      time: 0,
      auxdata: new Array(9).fill(0),
      kind: 'off',
    });

    this.touchProgramData(true);
  }

  cancelNewProgram(): void {
    if (!this.newProgram) return;
    this.programDataFormated.pop();
    this.touchProgramData();
    this.newProgram = false;
    this.changesProgram = false;
  }

  saveProgram(): void {
    this.loading = true;

    const programsToSave = [...this.programDataFormated];
    let pending = programsToSave.length;
    let hasError = false;

    if (pending === 0) {
      this.loading = false;
      this.changesProgram = false;
      return;
    }

    programsToSave.forEach((program, index) => {
      const body = {
        [`${index}`]: this._dlScheduleService.programToRaw(program),
      };

      this._device.setModuleCfg(this.device, body, 'schedule').subscribe({
        next: () => {
          pending--;
          if (pending === 0 && !hasError) {
            this.onProgramsSaved();
          }
        },
        error: () => {
          hasError = true;
          this.error = this.t('programSaveError');
          this.loading = false;
          setTimeout(() => {
            this.error = '';
          }, 3000);
        },
      });
    });
  }

  restoreProgram(): void {
    this.changesProgram = false;
    this.programDataFormated = JSON.parse(JSON.stringify(this.programdataFormatedOriginal));
    this.touchProgramData();
    this.editedProgramIds.clear();
    this.highlightedProgramIndex = null;
  }

  addPeriod(): void {
    this.changesHoliday = true;
    this.holidayEnable = true;

    if (this.holidayData.periods.length >= 4 && this.holidayData.periods.every((period: any) => period.enable)) {
      return;
    }

    for (const period of this.holidayData.periods) {
      if (!period.enable) {
        period.enable = 1;
        this.openCalendarModal(this.holidayData.periods.indexOf(period));
        break;
      }
    }
  }

  deletePeriod(index: number): void {
    this.changesHoliday = true;
    this.holidayData.periods[index].enable = 0;
  }

  openCalendarModal(editSelectedIndex?: number): void {
    const modalRef = this._modalService.open(ModalHolidayCalendarComponent, {
      centered: true,
      scrollable: true,
      size: 'xl',
      fullscreen: true,
    });

    modalRef.componentInstance.holidayData = this.holidayData.periods;
    if (editSelectedIndex !== undefined) {
      modalRef.componentInstance.selectedIndex = editSelectedIndex;
    }

    modalRef.result.then(
      (result) => {
        this.holidayData.periods = result;
        this.changesHoliday = true;
        this.todayPeriod = this._ancDataService.isTodayInAnyPeriod(this.holidayData?.periods);
        this.refreshScheduleData();
      },
      () => undefined
    );
  }

  restoreHoliday(): void {
    this.changesHoliday = false;
    this.holidayData = JSON.parse(JSON.stringify(this.originalHolidayData));
    this.todayPeriod = this._ancDataService.isTodayInAnyPeriod(this.holidayData?.periods);
    this.refreshScheduleData();
  }

  saveHoliday(): void {
    this.loading = true;
    const body: { [key: string]: number[] } = {};

    for (const holiday of this.holidayData.periods) {
      Object.assign(body, this.holidayToRaw(holiday));
    }

    this._device.setModuleCfg(this.device, body, 'holiday').subscribe({
      next: () => {
        this.holidayData = { ...this.holidayData };
        this.originalHolidayData = JSON.parse(JSON.stringify(this.holidayData));
        this.changesHoliday = false;
        this.successMessage = true;
        this.loading = false;
        this.todayPeriod = this._ancDataService.isTodayInAnyPeriod(this.holidayData?.periods);
        this.refreshScheduleData();
        setTimeout(() => {
          this.successMessage = false;
        }, 3000);
      },
      error: () => {
        this.error = this.t('holidaySaveError');
        this.loading = false;
        setTimeout(() => {
          this.error = '';
        }, 3000);
      },
    });
  }

  getKindLabel(kind: DlProgramKind): string {
    switch (kind) {
      case 'on': return this.t('on');
      case 'pulse': return this.t('programKind_pulse');
      case 'cycle': return this.t('programKind_cycle');
      default: return this.t('off');
    }
  }

  getDurationLabel(seconds: number): string {
    return this._dlScheduleService.formatDurationLabel(seconds);
  }

  getPulseDurationLabel(seconds: number): string {
    return this._dlScheduleService.formatPulseDurationLabel(seconds);
  }

  getChannelLabel(channel: number): string {
    if (channel === 1) return 'C1';
    if (channel === 2) return 'C2';
    return this.t('channelBoth');
  }

  isProgramEdited(programId: unknown): boolean {
    const id = Number(programId);
    return Number.isFinite(id) && this.editedProgramIds.has(id);
  }

  onEditDraftTypeChange(): void {
    if (!this.editProgramDraft) return;

    const previousKind = this.editProgramDraft.kind;
    const type = Number(this.editProgramDraft.type) || 0;
    this.editProgramDraft.mode = this._dlScheduleService.normalizeModeForType(type);
    this.editProgramDraft.kind = this._dlScheduleService.resolveKindFromType(
      type,
      this.editProgramDraft.mode
    );

    if (type === 3 && previousKind !== 'cycle') {
      this.editProgramDraft.cycleEndDaymask = this.editProgramDraft.daymask;
      this.editProgramDraft.cycleOnSeconds = Math.max(1, Number(this.editProgramDraft.cycleOnSeconds) || 60);
      this.editProgramDraft.cycleOffSeconds = Math.max(1, Number(this.editProgramDraft.cycleOffSeconds) || 60);
      if (!this.editProgramDraft.endTimeStr) {
        this.editProgramDraft.endTimeStr = '00:00';
      }
    }
  }

  private onProgramsSaved(): void {
    this.programdataFormatedOriginal = JSON.parse(JSON.stringify(this.programDataFormated));
    this.changesProgram = false;
    this.successMessage = true;
    this.editedProgramIds.clear();
    this.highlightedProgramIndex = null;
    this.loading = false;
    setTimeout(() => {
      this.successMessage = false;
    }, 3000);
  }

  private enrichProgram(program: DlProgram): DlProgramEdit {
    const auxdata = [...(program.auxdata || [])];
    while (auxdata.length < 9) auxdata.push(0);

    const startMinute = this._dlScheduleService.getStartMinute({ ...program, auxdata });
    const kind = this._dlScheduleService.resolveKindFromType(
      Number(program.type) || 0,
      Number(program.mode) || 0
    );
    const enriched: DlProgramEdit = {
      ...program,
      auxdata,
      kind,
      timeStr: startMinute !== null ? this._dlScheduleService.formatMinuteLabel(startMinute) : '00:00',
      endTimeStr: '00:00',
      pulseDurationSeconds: 60,
      cycleOnSeconds: 60,
      cycleOffSeconds: 60,
      cycleEndDaymask: program.daymask,
    };

    if (kind === 'pulse') {
      const pulseSeconds = this._dlScheduleService.getPulseDurationSeconds({ ...program, auxdata });
      enriched.pulseDurationSeconds = pulseSeconds > 0 ? pulseSeconds : 60;
    }

    if (kind === 'cycle') {
      const endMinute = this._dlScheduleService.getCycleEndMinute({ ...program, auxdata });
      const { onSeconds, offSeconds } = this._dlScheduleService.getCycleOnOffSeconds({ ...program, auxdata });

      enriched.endTimeStr = endMinute !== null
        ? this._dlScheduleService.formatMinuteLabel(endMinute)
        : '00:00';
      enriched.cycleOnSeconds = onSeconds || 60;
      enriched.cycleOffSeconds = offSeconds || 60;
      enriched.cycleEndDaymask = this._dlScheduleService.getCycleEndDaymask({ ...program, auxdata });
    }

    return enriched;
  }

  private touchProgramData(markChanged = false): void {
    this.activePrograms = (this.programDataFormated || []).filter(
      (program) => Number(program?.id) !== -1
    );
    this.refreshScheduleData();
    if (markChanged) {
      this.changesProgram = true;
    }
  }

  private refreshScheduleData(): void {
    this.scheduleData = this.activePrograms.flatMap((program) => this.expandProgramForScheduler(program));
    this.updateRelayStats();
  }

  private expandProgramForScheduler(program: DlProgramEdit): any[] {
    const startMinute = this._dlScheduleService.getStartMinute(program);
    if (startMinute === null) return [];

    const base = {
      id: program.id,
      channel: program.channel,
      festive: program.festive,
      mode: 16,
    };

    if (program.kind === 'pulse') {
      const durationMin = Math.max(
        1,
        Math.ceil(this._dlScheduleService.getPulseDurationSeconds(program) / 60)
      );
      const endMinute = Math.min(DL_MINUTES_PER_DAY - 1, startMinute + durationMin);
      const events: any[] = [{
        ...base,
        type: 1,
        time: startMinute,
        daymask: program.daymask,
        dlKind: 'pulse',
        dlEdge: 'start',
      }];

      if (endMinute > startMinute) {
        events.push({
          ...base,
          type: 0,
          time: endMinute,
          daymask: program.daymask,
          dlKind: 'pulse',
          dlEdge: 'end',
          dlDraggable: false,
        });
      }

      return events;
    }

    if (program.kind === 'cycle') {
      const endMinute = this._dlScheduleService.getCycleEndMinute(program);
      const endDaymask = this._dlScheduleService.getCycleEndDaymask(program);
      const weekWrap = this._dlScheduleService.isCycleWeekWrap(program);
      const overnightClock = endMinute !== null && endMinute <= startMinute;

      if (!weekWrap && endMinute !== null) {
        return [
          {
            ...base,
            type: 1,
            time: startMinute,
            daymask: program.daymask,
            dlKind: 'cycle',
            dlEdge: 'start',
          },
          {
            ...base,
            type: 0,
            time: endMinute,
            daymask: endDaymask,
            dlKind: 'cycle',
            dlEdge: 'end',
          },
        ];
      }

      const events: any[] = [{
        ...base,
        type: 1,
        time: startMinute,
        daymask: program.daymask,
        dlKind: 'cycle',
        dlEdge: 'start',
      }];

      if (endMinute !== null && endDaymask) {
        events.push({
          ...base,
          type: 0,
          time: endMinute,
          daymask: endDaymask,
          dlKind: 'cycle',
          dlEdge: 'end',
        });

        if (weekWrap && overnightClock) {
          events.push({
            ...base,
            type: 1,
            time: 0,
            daymask: endDaymask,
            dlKind: 'cycle',
            dlEdge: 'start',
            dlDraggable: false,
          });
        }
      }

      return events;
    }

    return [{
      ...base,
      type: program.type,
      time: startMinute,
      daymask: program.daymask,
      dlKind: 'switch',
      dlEdge: 'start',
    }];
  }

  private updateRelayStats(): void {
    const activeFestive = this.todayPeriod?.periodIndex ?? 0;
    const chartData = this._dlScheduleService.buildWeekChartData(
      this.activePrograms,
      activeFestive,
      this.selectedOutput
    );

    const todayIndex = this.getTodayDayIndex();
    const todayStart = todayIndex * DL_MINUTES_PER_DAY;
    const todayOnMinutes = chartData
      .slice(todayStart, todayStart + DL_MINUTES_PER_DAY)
      .filter((value) => value === 1).length;

    let weekOnMinutes = 0;
    for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
      const offset = dayIndex * DL_MINUTES_PER_DAY;
      weekOnMinutes += chartData
        .slice(offset, offset + DL_MINUTES_PER_DAY)
        .filter((value) => value === 1).length;
    }

    this.todayHours = (todayOnMinutes / 60).toFixed(1);
    this.annualHours = Math.round((weekOnMinutes / 7) * 365 / 60).toString();
  }

  private getTodayDayIndex(): number {
    const jsDay = new Date().getDay();
    return jsDay === 0 ? 6 : jsDay - 1;
  }

  private getNextProgramId(): number {
    const usedIds = new Set(
      (this.programDataFormated || [])
        .map((program) => Number(program.id))
        .filter((id) => Number.isFinite(id) && id >= 0)
    );

    for (let id = 0; id < 40; id++) {
      if (!usedIds.has(id)) return id;
    }

    return this.programDataFormated.length;
  }

  private minutesFromTimeInput(value: unknown): number | null {
    return this._dlScheduleService.parseTimeInputToMinutes(value);
  }

  /** Lee la hora del input nativo (type="time") por si ngModel no sincronizó antes de guardar. */
  private readTimeInputMinutes(inputId: string, fallback: unknown): number | null {
    const input = document.getElementById(inputId) as HTMLInputElement | null;
    const raw = input?.value?.trim() ? input.value : fallback;
    return this._dlScheduleService.parseTimeInputToMinutes(raw);
  }

  private markProgramAsEdited(programId: number): void {
    if (!Number.isFinite(programId) || programId < 0) return;
    this.editedProgramIds.add(programId);
    this.changesProgram = true;
  }

  private highlightProgramRow(index: number): void {
    if (!Number.isInteger(index) || index < 0) return;

    this.highlightedProgramIndex = index;

    if (this.highlightTimeoutId) {
      clearTimeout(this.highlightTimeoutId);
    }

    this.highlightTimeoutId = setTimeout(() => {
      this.highlightedProgramIndex = null;
      this.highlightTimeoutId = null;
    }, 2500);
  }

  private holidayToRaw(holiday: any): { [key: string]: number[] } {
    const raw = new Array(9);

    raw[0] = holiday.startDay;
    raw[1] = holiday.startMonth;
    raw[2] = holiday.startHour;
    raw[3] = holiday.startMinute;
    raw[4] = holiday.endDay;
    raw[5] = holiday.endMonth;
    raw[6] = holiday.endHour;
    raw[7] = holiday.endMinute;
    raw[8] = holiday.enable;

    return { [`period${holiday.period}`]: raw };
  }
}
